import React from 'react';

import {
    BasicForm,
    IconTextButton,
    Modal,
    notification,
    SelectFormItem,
    showConfirmDialog,
    showInformationDialog,
    SimpleFileUploadItem,
    TextFieldItem
} from 'ui-lib';

import { IDeviceGroupDataInput, TUpdateDeviceGroupOptions , IDeviceGroupOutOfServiceSettingsProps , ITimeInterval } from '../../../../dto/device-group-dto';
import { getErrorMessage, isNullOrEmpty, TIMEZONES } from 'common-utils';
import { inputLabelRule } from '../../components/form-utils';
import { confirmCancel } from '../../admin/utils';
import { AssignDevicesComponent } from './assign-devices';
import { useManageDeviceGroupContext } from './';
import { useApiClientStore, useDeviceGroupsStore, useDeviceStore } from '../../../../store';
import { AttributeFormItem, getDisplayAttribute, getIoTAttribute } from '../../components/attribute-form-item';
import { OutOfServiceFormItem , getDisplayOutOfServiceSettings } from '../../components/out-of-service-form-item';
import { observer } from 'mobx-react';
import { ImportDevicesSummaryTable, TDisplayImportDeviceProps } from './import-devices-summary-table';
import { MoveDevicesSummaryTable } from './move-devices-summary-table';
import { useNavigate } from 'react-router-dom';
import { PRIVATE_ROUTES } from 'store/private-routes';
import manageDeviceGroupCss from './manage-device-group.css';
import { useDeviceGroupPermissionContext } from 'contexts/permissions/device-group-permission.context';
import { formatDate , DATE_TIME_FORMAT , isValidShortTime } from 'common-utils/date-utils';
import { usePromiseMemo } from 'utils/common-utils';
import { ImportDeviceStatus } from 'dto/device-dto';

interface IDeviceGroupComponentOptions {
    title: string;
    editMode?: boolean;
    deviceGroupLabel?: string;
    deviceGroupDescription?: string;
    deviceGroupPlatform?: string;
    deviceGroupTimezone?: string;
    deviceGroupAttribute?: IDeviceGroupDataInput;
    deviceGroupOutOfServiceSettings?: IDeviceGroupOutOfServiceSettingsProps;
    onSave: (params: TUpdateDeviceGroupOptions) => any;
    onCancel?: () => void;
    extra: React.ReactNode | null;
}

export const ModifyDeviceGroupComponent = () => {
    const { assignDevicesStore, clientAccountId, clientDeviceGroup, childGroup, onUpdating, navigation } =
        useManageDeviceGroupContext();
    const { canAssignDevices, canManageDeviceGroup, runHandler } = useDeviceGroupPermissionContext();
    const navigate = useNavigate();

    const [assignDeviceSummaryModalProps, setAssignDeviceSummaryModalProps] = React.useState({
        visible: false,
        deviceGroupName: undefined,
        onSave: undefined,
        redirectToDeviceGroupsTable: undefined,
        redirectToDeploymentPage: undefined,
    });

    const onDisplayModal = async (
        deviceGroupName: string,
        onSave: () => Promise<unknown>,
        redirectToDeviceGroupsTable: () => void,
        redirectToDeploymentPage?: () => void,
    ): Promise<void> => {
        const onSaveDeviceGroup = () =>
            runHandler('Device Groups', onSave, () => {
                return; // do nothing
            });
        if (canAssignDevices && !isNullOrEmpty(assignDevicesStore.changeGroupDevices)) {
            return setAssignDeviceSummaryModalProps({
                ...assignDeviceSummaryModalProps,
                onSave: onSaveDeviceGroup,
                visible: true,
                deviceGroupName,
                redirectToDeploymentPage,
                redirectToDeviceGroupsTable,
            });
        }
        await onSaveDeviceGroup();
        return redirectToDeviceGroupsTable();
    };

    const deviceGroupsStore = useDeviceGroupsStore();

    const deviceGroupActions = React.useMemo<IDeviceGroupComponentOptions>(() => {
        if (childGroup) {
            return {
                editMode: true,
                title: `Edit ${childGroup.deviceGroupLabel} device group`,
                ...childGroup,
                onSave: (params: TUpdateDeviceGroupOptions): unknown => {
                    const initialTimezone = childGroup.deviceGroupTimezone || '';
                    const newTimezone = params.deviceGroupTimezone || '';
                    params.deviceGroupLabel = params.deviceGroupLabel || '';
                    params.deviceGroupDescription = params.deviceGroupDescription || '';
                    params.deviceGroupOutOfServiceSettings = params.deviceGroupOutOfServiceSettings || {
                        outOfServiceWindows: [],
                    };
                    if (initialTimezone && !newTimezone) {
                        onUpdating(false);
                        return showInformationDialog({
                            modalType: 'error',
                            title: 'Error',
                            content: 'Cannot set an empty timezone.',
                        });
                    };

                    return onDisplayModal(
                        childGroup.deviceGroupName,
                        () => {
                            return deviceGroupsStore.updateDeviceGroupByName(clientAccountId, childGroup.deviceGroupName, {
                                ...params,
                                deviceGroupTimezone: newTimezone,
                            });
                        },
                        navigation.toDeviceGroupsTable,
                        childGroup.deploymentGroupId
                            ? (): void => {
                                  navigate(PRIVATE_ROUTES.SOFTWARE_MANAGEMENT_DEPLOYMENTS_VIEW.path, {
                                      state: {
                                          clientAccountId: clientAccountId,
                                          deploymentGroupId: childGroup.deploymentGroupId,
                                      },
                                  });
                              }
                            : undefined,
                    );
                },
                extra: null,
            };
        }

        return {
            title: `Create device group under ${clientDeviceGroup.deviceGroupLabel}`,
            deviceGroupPlatform: deviceGroupsStore.supportedPlatforms[0]?.code ?? 'ASSURE',
            onSave: function (params: TUpdateDeviceGroupOptions): Promise<void> {
                const deviceGroupName = assignDevicesStore.currentGroupName;

                return onDisplayModal(
                    deviceGroupName,
                    () => {
                        return deviceGroupsStore.createDeviceGroupByName(clientAccountId, deviceGroupName, {
                            ...params,
                            deviceParentGroupName: clientDeviceGroup.deviceGroupName,
                        });
                    },
                    navigation.toDeviceGroupsTable,
                );
            },
            extra: null,
        };
    }, []);

    const onSave = async (params: TUpdateDeviceGroupOptions) => {
        onUpdating(true);
        try {
            if (
                !!clientDeviceGroup.childrenGroups?.find((group) => {
                    return (
                        group.deviceGroupLabel === params.deviceGroupLabel &&
                        childGroup?.deviceGroupName !== group.deviceGroupName
                    );
                })
            ) {
                onUpdating(false);
                return showInformationDialog({
                    modalType: 'error',
                    title: 'Error',
                    content: `Device group with label "${params.deviceGroupLabel}" already exists.`,
                });
            }
            await deviceGroupActions.onSave(params);
        } catch (err) {
            notification.error({
                message: 'Save device group error',
                description: getErrorMessage(err),
            });
        } finally {
            onUpdating(false);
        }
    };

    const hideModal = () => {
        setAssignDeviceSummaryModalProps((prevProps) => ({ ...prevProps, visible: false }));
    }

    return (
        <React.Fragment>
            <CreateOrEditDeviceGroupComponent
                {...deviceGroupActions}
                onSave={onSave}
                onCancel={() => {
                    assignDevicesStore.setCurrentDeviceGroup(undefined);
                    navigation.toDeviceGroupsTable();
                }}
            />
            <Modal width={1400} isVisible={assignDeviceSummaryModalProps.visible}>
                <MoveDevicesSummaryTable
                    assignDeviceStore={assignDevicesStore}
                    deviceGroupName={assignDeviceSummaryModalProps.deviceGroupName}
                    onSave={assignDeviceSummaryModalProps.onSave}
                    onClose={hideModal}
                    redirectToDeviceGroupsTable={() => {
                        hideModal();
                        assignDeviceSummaryModalProps.redirectToDeviceGroupsTable();
                    }}
                    redirectToDeploymentPage={assignDeviceSummaryModalProps.redirectToDeploymentPage ? () => {
                        hideModal();
                        assignDeviceSummaryModalProps.redirectToDeploymentPage();
                    } : undefined}
                />
            </Modal>
        </React.Fragment>
    );
};

export const CreateOrEditDeviceGroupComponent = observer((props: IDeviceGroupComponentOptions) => {
    const { assignDevicesStore: store, clientAccountId, clientDeviceGroup, onUpdating, isDeviceTypeSupported } = useManageDeviceGroupContext();
    const { canAssignDevices, canManageDeviceGroup, runHandler } = useDeviceGroupPermissionContext();
    const deviceGroupsStore = useDeviceGroupsStore();
    const deviceGroupApiClient = useApiClientStore().apiClients.devicegroups;

    const [importDeviceProps, setImportDeviceProps] = React.useState<{
        visible: boolean;
        devices: TDisplayImportDeviceProps[];
    }>({ visible: false, devices: undefined });

    const [fileList, setFileList] = React.useState<File[]>([]);
    const [platform, setPlatform] = React.useState<string>(props.deviceGroupPlatform);
    const onSubmit = async (values): Promise<void> => {
        if (values.deviceGroupAttribute) {
            values.deviceGroupAttribute = getIoTAttribute(values.deviceGroupAttribute);
        }
        if (values.deviceGroupOutOfServiceSettings) {
            const outOfServiceWindows: ITimeInterval[] = values.deviceGroupOutOfServiceSettings.map((item) => ({
                startTime: isValidShortTime(item.startTime)
                    ? item.startTime
                    : formatDate(item.startTime, DATE_TIME_FORMAT.SHORT_TIME),
                endTime: isValidShortTime(item.endTime)
                    ? item.endTime
                    : formatDate(item.endTime, DATE_TIME_FORMAT.SHORT_TIME),
            }));
            values.deviceGroupOutOfServiceSettings = { outOfServiceWindows };
        }

        await props.onSave(values);
    };

    const handleUpload = async (files: File[]): Promise<void> => {
        await runHandler('Move Devices', async () => {
            onUpdating(true);
            try {
                const result = await deviceGroupApiClient.importDevices(
                    clientAccountId,
                    clientDeviceGroup.deviceGroupName,
                    store.currentGroupName,
                    files,
                );
                if (!isNullOrEmpty(result.error)) throw new Error(result.error);
                setImportDeviceProps({
                    visible: true,
                    devices: result.devices.map((device) => ({
                        ...device,
                        status: isDeviceTypeSupported(props.deviceGroupPlatform, device.deviceType)
                            ? device.status
                            : ImportDeviceStatus.NONIMPORTABLE,
                    })),
                });
            } catch (err) {
                notification.error({
                    message: 'Import Error',
                    description: getErrorMessage(err),
                });
            } finally {
                setFileList([]);
                onUpdating(false);
            }
        });
    };

    return (
        <React.Fragment>
            <Modal width={1400} isVisible={importDeviceProps.visible}>
                <ImportDevicesSummaryTable
                    organisationId={clientAccountId}
                    devices={importDeviceProps.devices}
                    assignDeviceStore={store}
                    deviceGroup={deviceGroupsStore.getDeviceGroupByName(store.currentGroupName)}
                    onClose={(): void => setImportDeviceProps({ visible: false, devices: undefined })}
                />
            </Modal>
            <BasicForm
                cardPros={{
                    title: <h3>{props.title}</h3>,
                    extra: props.extra,
                }}
                onSubmit={onSubmit}
                items={[
                    <TextFieldItem
                        key='deviceGroupLabel'
                        code='deviceGroupLabel'
                        label='Device group label'
                        labelAlign='right'
                        isRequired={true}
                        rule={inputLabelRule(128)}
                        initialValue={props.deviceGroupLabel}
                        disabled={!canManageDeviceGroup}
                    />,
                    <TextFieldItem
                        key='deviceGroupDescription'
                        code='deviceGroupDescription'
                        label='Device group description'
                        labelAlign='right'
                        isRequired={false}
                        initialValue={props.deviceGroupDescription}
                        disabled={!canManageDeviceGroup}
                    />,
                    <SelectFormItem
                        key='deviceGroupPlatform'
                        code='deviceGroupPlatform'
                        label='Platform'
                        dataSource={deviceGroupsStore.supportedPlatforms.map((item) => ({
                            value: item.code,
                            label: item.name,
                        }))}
                        placeholder='Please select platform'
                        selectedValue={platform}
                        initialValue={platform}
                        onChange={(value: string) => setPlatform(value)}
                        disabled={!canManageDeviceGroup || props.editMode}
                        labelAlign='right'
                    />,
                    <SelectFormItem
                        key='deviceGroupTimezone'
                        code='deviceGroupTimezone'
                        label='Timezone'
                        dataSource={[{ value: '', label: '' }, ...TIMEZONES]}
                        placeholder='Please select timezone'
                        selectedValue={props.deviceGroupTimezone}
                        initialValue={props.deviceGroupTimezone}
                        disabled={!canManageDeviceGroup}
                        showSearch={true}
                        labelAlign='right'
                    />,
                    <AttributeFormItem
                        key='deviceGroupAttibute'
                        code='deviceGroupAttibute'
                        label='Device group attribute'
                        className={manageDeviceGroupCss.attribute}
                        initialValue={getDisplayAttribute(props.deviceGroupAttribute, ['type'])}
                        readonly={!canManageDeviceGroup}
                    />,
                    <OutOfServiceFormItem
                        key='deviceGroupOutOfServiceSettings'
                        code='deviceGroupOutOfServiceSettings'
                        label='Device Group Out Of Service Window'
                        className={manageDeviceGroupCss.attribute}
                        initialValue={getDisplayOutOfServiceSettings(props.deviceGroupOutOfServiceSettings)}
                        readonly={!canManageDeviceGroup}
                    />,
                    <SimpleFileUploadItem
                        key='csv-upload'
                        labelAlign='right'
                        simpleFileUploadProps={{
                            fileList,
                            setFileList,
                            className: manageDeviceGroupCss['csv-import-box'],
                            afterUploadChecking: handleUpload,
                            disabled: !canAssignDevices,
                            button: {
                                label: 'Select file',
                                type: 'primary',
                            },
                        }}
                        code='csv-upload'
                        label='Import devices'
                    />,
                    <AssignDevicesComponent key='assign-devices-component' platform={platform} />,
                    <div className={manageDeviceGroupCss.buttons} key='action-buttons'>
                        <IconTextButton key='cancel' label='Cancel' onClick={() => confirmCancel(props.onCancel)} />
                        <IconTextButton key='save' label='Save' type='primary' htmlType='submit' hidden={!canAssignDevices && !canManageDeviceGroup} />
                    </div>,
                ]}
            />
        </React.Fragment>
    );
});
